<template>
  <div class="multi-field-response">
    <div class="field-header">
      <h3 class="field-label">
        {{ field.label }}
        <span v-if="field.required" class="required-indicator">*</span>
      </h3>
    </div>

    <div v-if="field.description" class="field-description">
      {{ field.description }}
    </div>

    <!-- Existing entries list -->
    <div v-if="entries.length > 0" class="entries-list">
      <v-expansion-panels v-model="expandedPanels" multiple>
        <v-expansion-panel
          v-for="(entry, entryIndex) in entries"
          :key="entry.id"
        >
          <v-expansion-panel-header>
            <div class="d-flex align-center">
              <span>Entry {{ entryIndex + 1 }}</span>
              <v-spacer></v-spacer>
              <v-btn
                icon
                x-small
                color="error"
                @click.stop="removeEntry(entryIndex)"
                :disabled="(field.required && entries.length <= (field.minEntries || 1)) || disabled"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div v-for="(subField, subFieldIndex) in field.subFields" :key="subFieldIndex" class="mb-4">
              <div class="d-flex align-center mb-1">
                <span class="font-weight-medium">{{ subField.label }}</span>
                <span v-if="subField.required" class="required-indicator ml-1">*</span>
              </div>

              <!-- TEXT type subfield -->
              <v-text-field
                v-if="subField.fieldType === FieldType.TEXT"
                v-model="entry.subFieldValues[subField.name]"
                outlined
                dense
                :placeholder="subField.hint || 'Enter text...'"
                :rules="getSubFieldRules(subField)"
                @input="onSubFieldInput(entry, subField.name, $event)"
                :readonly="disabled"
              ></v-text-field>

              <!-- TEXTAREA type subfield -->
              <v-textarea
                v-else-if="subField.fieldType === FieldType.TEXTAREA"
                v-model="entry.subFieldValues[subField.name]"
                outlined
                dense
                :placeholder="subField.hint || 'Enter text...'"
                :rules="getSubFieldRules(subField)"
                rows="3"
                auto-grow
                @input="onSubFieldInput(entry, subField.name, $event)"
                :readonly="disabled"
              ></v-textarea>

              <!-- NUMBER type subfield -->
              <v-text-field
                v-else-if="subField.fieldType === FieldType.NUMBER"
                v-model.number="entry.subFieldValues[subField.name]"
                outlined
                dense
                type="number"
                :placeholder="subField.hint || 'Enter a number...'"
                :rules="getSubFieldRules(subField)"
                :min="subField.minValue"
                :max="subField.maxValue"
                :step="subField.stepValue || 1"
                @input="onSubFieldInput(entry, subField.name, $event)"
                :readonly="disabled"
              ></v-text-field>

              <!-- DATE type subfield -->
              <v-menu
                v-else-if="subField.fieldType === FieldType.DATE"
                v-model="dateMenus[`${entry.id}_${subField.name}`]"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
                :disabled="disabled"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="entry.subFieldValues[subField.name]"
                    outlined
                    dense
                    readonly
                    :placeholder="subField.hint || 'Select a date...'"
                    :rules="getSubFieldRules(subField)"
                    v-bind="attrs"
                    v-on="on"
                    @change="updateResponse"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="entry.subFieldValues[subField.name]"
                  :min="subField.minDate"
                  :max="subField.maxDate"
                  @input="dateMenus[`${entry.id}_${subField.name}`] = false; updateResponse()"
                  :readonly="disabled"
                ></v-date-picker>
              </v-menu>

              <!-- RATING type subfield -->
              <div v-else-if="subField.fieldType === FieldType.RATING">
                <v-rating
                  v-model="entry.subFieldValues[subField.name]"
                  :length="subField.maxRating || 5"
                  color="amber"
                  background-color="grey lighten-1"
                  empty-icon="mdi-star-outline"
                  half-increments
                  hover
                  dense
                  @input="updateResponse"
                  :readonly="disabled"
                ></v-rating>
                <div v-if="subField.required && !entry.subFieldValues[subField.name]" class="error--text text-caption mt-1">
                  This field is required
                </div>
              </div>

              <!-- SELECT type subfield -->
              <v-select
                v-else-if="subField.fieldType === FieldType.SINGLE_SELECT"
                v-model="entry.subFieldValues[subField.name]"
                outlined
                dense
                :items="subField.options || []"
                item-text="label"
                item-value="id"
                :placeholder="subField.hint || 'Select an option...'"
                :rules="getSubFieldRules(subField)"
                @change="updateResponse"
                :readonly="disabled"
              ></v-select>

              <!-- CHECKBOX type subfield -->
              <v-checkbox
                v-else-if="subField.fieldType === FieldType.CHECKBOX"
                v-model="entry.subFieldValues[subField.name]"
                :label="subField.hint || 'Check if applicable'"
                @change="updateResponse"
                :readonly="disabled"
              ></v-checkbox>

              <!-- RADIO type subfield -->
              <v-radio-group
                v-else-if="subField.fieldType === FieldType.RADIO"
                v-model="entry.subFieldValues[subField.name]"
                @change="updateResponse"
                :rules="getSubFieldRules(subField)"
                :readonly="disabled"
              >
                <v-radio
                  v-for="option in subField.options"
                  :key="option.value"
                  :label="option.label"
                  :value="option.id"
                ></v-radio>
              </v-radio-group>

              <!-- MULTI_SELECT type subfield -->
              <v-select
                v-else-if="subField.fieldType === FieldType.MULTI_SELECT"
                v-model="entry.subFieldValues[subField.name]"
                outlined
                dense
                multiple
                chips
                :items="subField.options || []"
                item-text="label"
                item-value="id"
                :placeholder="subField.hint || 'Select options...'"
                :rules="getSubFieldRules(subField)"
                @change="updateResponse"
                :readonly="disabled"
              ></v-select>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>

    <!-- Entry management -->
    <div class="d-flex justify-space-between align-center mt-3">
      <v-btn
        color="primary"
        text
        @click="addEntry"
        :disabled="(field.maxEntries !== undefined && entries.length >= field.maxEntries) || disabled"
      >
        <v-icon left>mdi-plus</v-icon>
        Add Entry
      </v-btn>
      <div class="text-caption">
        <span :class="{'error--text': !isValidEntriesCount}">
          {{ entries.length }} entries
          <span v-if="field.minEntries !== undefined">(min: {{ field.minEntries }})</span>
          <span v-if="field.maxEntries !== undefined">(max: {{ field.maxEntries }})</span>
        </span>
      </div>
    </div>

    <!-- Comment section -->
    <v-textarea
      v-model="localResponse.comment"
      :readonly="disabled"
      outlined
      dense
      label="Comment (Optional)"
      placeholder="Add any additional comments here..."
      rows="2"
      auto-grow
      class="mt-3"
      @input="onCommentInput($event)"
    ></v-textarea>
  </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid';
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { MultiFieldValueDTO, Field, SubField } from '@/shared/types/aperTemplate';
import { FieldType } from '@/shared/types/aperTemplate';
import { ResponseDTO } from '@/shared/apis/aper/aperEvaluations';

@Component
export default class MultiFieldResponse extends Vue {
  @Prop({ type: Object, required: true }) field;
  @Prop({ type: Object, required: false }) response;
  @Prop({ type: Boolean, required: false }) disabled;

  FieldType = FieldType;

  entries = [];
  localResponse = {
    fieldId: 0,
    multiFieldValues: [],
    comment: ''
  };

  expandedPanels = [0];  // Local state for entries
  dateMenus = {};
  responseInitialized = false;

  created() {
    this.initializeResponse();
    this.responseInitialized = true;
  }

  @Watch('response')
  onResponseChange() {
    this.initializeResponse();
    this.responseInitialized = true
  }

  initializeResponse() {
    if (this.response) {
      //@ts-ignore
      this.localResponse = { ...this.response };
      this.entries = this.response.multiFieldValues || [];

      // Ensure at least minimum entries
      this.ensureMinEntries();
    } else {
      this.localResponse = {
        fieldId: this.field.id || 0,
        multiFieldValues: [],
        comment: ''
      };
      this.entries = [];

      // Initialize with minimum entries
      this.ensureMinEntries();
    }
  }

  ensureMinEntries() {
    const minEntries = this.field.minEntries || 1;
    while (this.entries.length < minEntries) {
      this.addEntry();
    }
  }

  addEntry() {
    const newEntry = {
      id: uuidv4(),
      subFieldValues: {}
    };

    // Initialize empty values for all subfields
    if (this.field.subFields) {
      for (const subField of this.field.subFields) {
        // Initialize with appropriate default values based on field type
        switch (subField.fieldType) {
          case FieldType.NUMBER:
            newEntry.subFieldValues[subField.name] = null;
            break;
          case FieldType.RATING: 
            newEntry.subFieldValues[subField.name] = 0;
            break;
          case FieldType.CHECKBOX:
            newEntry.subFieldValues[subField.name] = false;
            break;
          case FieldType.DATE:
            newEntry.subFieldValues[subField.name] = ''; 
            break;
          case FieldType.SINGLE_SELECT:
          case FieldType.RADIO:
            newEntry.subFieldValues[subField.name] = null;  
            break;
          case FieldType.MULTI_SELECT:
            newEntry.subFieldValues[subField.name] = [];
            break;
          default:
            newEntry.subFieldValues[subField.name] = '';
        }
      }
    }

    this.entries.push(newEntry);
    this.updateResponse();

    // Expand the newly added panel
    this.$nextTick(() => {
      this.expandedPanels = [...this.expandedPanels, this.entries.length - 1];
    });
  }

  removeEntry(index) {
    this.entries.splice(index, 1);
    this.updateResponse();
  }

  getSubFieldRules(subField) {
    const rules = [];

    // Required validation
    if (subField.required) {
      rules.push(v => {
        // Handle different field types for required validation
        if (subField.fieldType === FieldType.MULTI_SELECT) {
          // For multi-select, check if array is empty
          if (!v || !Array.isArray(v) || v.length === 0) {
            return `${subField.label} is required`;
          }
        } else if (subField.fieldType === FieldType.CHECKBOX) {
          // For checkbox, check if it's checked (true)
          if (!v) {
            return `${subField.label} is required`;
          }
        } else {
          // For other field types, check for null, undefined, or empty string
          if (v === null || v === undefined || v === '') {
            return `${subField.label} is required`;
          }
        }
        return true;
      });
    }

    // Field type specific validations
    switch (subField.fieldType) {
      case FieldType.TEXT:
      case FieldType.TEXTAREA:
        if (subField.minLength !== undefined) {
          rules.push(v => !v || v.length >= subField.minLength ||
            `Minimum length is ${subField.minLength} characters`);
        }
        if (subField.maxLength !== undefined) {
          rules.push(v => !v || v.length <= subField.maxLength ||
            `Maximum length is ${subField.maxLength} characters`);
        }
        break;

      case FieldType.NUMBER:
        if (subField.minValue !== undefined) {
          rules.push(v => !v || v >= subField.minValue ||
            `Minimum value is ${subField.minValue}`);
        }
        if (subField.maxValue !== undefined) {
          rules.push(v => !v || v <= subField.maxValue ||
            `Maximum value is ${subField.maxValue}`);
        }
        break;
    }

    return rules;
  }

  get isValidEntriesCount() {
    if (this.field.minEntries !== undefined && this.entries.length < this.field.minEntries) {
      return false;
    }
    if (this.field.maxEntries !== undefined && this.entries.length > this.field.maxEntries) {
      return false;
    }
    return true;
  }

  onSubFieldInput(entry, fieldName, value) {
    this.$set(entry.subFieldValues, fieldName, value);
    this.updateResponse();
  }

  onCommentInput(value) {
    this.localResponse.comment = value;
    this.updateResponse();
  }

  updateResponse() {
    if(!this.responseInitialized) return
    this.localResponse.multiFieldValues = JSON.parse(JSON.stringify(this.entries));
    this.$emit('update:response', { ...this.localResponse });
  }
}
</script>

<style scoped>
.multi-field-response {
  width: 100%;
}

.field-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.field-label {
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  margin: 0 0 4px 0;
}

.required-indicator {
  color: #f44336;
  margin-left: 4px;
}

.field-description {
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 12px;
}

.entries-list {
  margin-top: 12px;
}
</style>
